import { FC, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { palletCheckRequest, stuffingRequest } from "api/requests";
import { Notification } from "components/LegacyUI/Notification/Notification";
import { StuffingCalculationLoader } from "components/Loading/StuffingCalculationLoader";
import loaderStyles from "components/Loading/StuffingCalculationLoader.module.scss";
import { Modal } from "components/UI/Modal";
import { ContainerPage } from "pages/ContainerPage";
import { ErrorMessages, StuffingInitialData, StuffingPage } from "pages/StuffingPage";
import {
  AppDispatch,
  getResponse,
  Group,
  hasInvalidRequiredCargoFields,
  hasInvalidRequiredPalletFields,
  Response,
  RootState,
  setErrorProduct,
  setPalletCheckLoader,
  setShowCargoValidation,
} from "store";
import { saveCalculationHistoryEntry } from "utils/calculationHistory.utils";
import { getReqBody, isLocalhost } from "utils/helper";
import { ErrorProductsProps, useAppHook } from "./App.hook";
import { App as AppShell } from "./components/App/App";
import { PackingList } from "./components/PackingList";

export type RouteType = "product" | "stuffing" | "container" | "";

type StuffingSuccessData = Response["data"] & {
  autoContainers?: RootState["root"]["autoCountContainers"];
};

type StuffingApiResponse = {
  data?: StuffingSuccessData | unknown[];
};

type AppProps = {
  platformId?: number;
  authTokenUrl?: string;
};

const routeToStep = (route: RouteType): 1 | 2 | 3 => {
  if (route === "container") return 2;
  if (route === "stuffing") return 3;
  return 1;
};

const collectStuffingErrors = (group: unknown[]): ErrorMessages[] => {
  const errors: ErrorMessages[] = [];
  group.forEach((item) => {
    const entry = item as { resultType?: string; group?: string; message?: string };
    if (entry.resultType === "break") {
      errors.push({ group: entry.group ?? "", message: entry.message ?? "" });
    }
  });
  return errors;
};

const App: FC<AppProps> = ({ platformId: _platformId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const root = useSelector((state: RootState) => state.root);
  const { groups, containers, autoContainers, palletCheckLoader } = root;
  const packingGroups = useSelector((state: RootState) => state.packingList.groups);
  const customPallets = useSelector((state: RootState) => state.packingList.customPallets);
  const unitSystem = useSelector((state: RootState) => state.app.unitSystem);

  const [route, setRoute] = useState<RouteType>("product");
  const [, setErrorModalVisible] = useState<boolean>(false);
  const [, setErrorProducts] = useState<ErrorProductsProps[]>([]);
  const [stuffingLoading, setStuffingLoading] = useState(false);
  const [stuffingInitial, setStuffingInitial] = useState<StuffingInitialData | null>(null);

  const { validate } = useAppHook();

  useLayoutEffect(() => {
    if (!isLocalhost) {
      localStorage.removeItem("s-token");
    }
  }, []);

  const setRouteHandler = async (routePath: RouteType) => {
    if (route === routePath || stuffingLoading || palletCheckLoader) return;
    const isPallet = groups.some((item: Group) => item.pallets);

    if (route === "product") {
      let switchPage = true;

      const cargoInvalid = hasInvalidRequiredCargoFields(packingGroups);
      const palletInvalid = hasInvalidRequiredPalletFields(packingGroups);

      if (cargoInvalid || palletInvalid) {
        dispatch(setShowCargoValidation(true));
        dispatch(setErrorProduct([]));
        return;
      }

      dispatch(setShowCargoValidation(false));

      if (isPallet) {
        dispatch(setPalletCheckLoader(true));
        try {
          const palletCheckResponse = await palletCheckRequest(getReqBody(root));
          const errors = palletCheckResponse.data?.errors ?? [];

          if (errors.length) {
            switchPage = false;
            dispatch(setErrorProduct(errors));
          } else {
            dispatch(setErrorProduct([]));
          }
        } catch (error) {
          // eslint-disable-next-line no-console -- surfaces pallet validation failures
          console.error("Pallet validation failed:", error);
          switchPage = false;
          Notification("Service message", "Pallet validation failed. Please try again.", "open");
        } finally {
          dispatch(setPalletCheckLoader(false));
        }
      }

      const errorProducts = validate(groups);
      if (errorProducts.length) {
        switchPage = false;
        let visibleErrorModal = false;

        groups.map(({ items }: Group) => {
          if (visibleErrorModal) return;
          if (items.length > 50) visibleErrorModal = true;
        });

        if (visibleErrorModal) {
          setErrorModalVisible(true);
          setErrorProducts(errorProducts);
        }
      }

      if (switchPage) {
        setRoute(routePath);
        dispatch(setErrorProduct([]));
      }
    } else {
      setRoute(routePath);
    }
  };

  const goToStuffing = async () => {
    if (stuffingLoading) return;

    setStuffingLoading(true);
    try {
      const res = (await stuffingRequest(getReqBody(root))) as StuffingApiResponse;

      if (!res?.data) {
        Notification("Service message", "No data received from stuffing request", "open");
        return;
      }

      if (Array.isArray(res.data)) {
        const errorMessages = collectStuffingErrors(res.data);
        setStuffingInitial({ data: false, errorMessages });
        setRoute("stuffing");
        return;
      }

      if (root.autoCountContainers.length) {
        res.data.autoContainers = root.autoCountContainers;
      }

      try {
        saveCalculationHistoryEntry({
          groups: packingGroups,
          customPallets,
          unitSystem,
        });
      } catch (historyError) {
        // eslint-disable-next-line no-console -- history must not block stuffing UX
        console.warn("Failed to save calculation history:", historyError);
      }

      const stuffingResponse: Response = { data: res.data };
      dispatch(getResponse(stuffingResponse));
      setStuffingInitial({ data: res.data, errorMessages: [] });
      setRoute("stuffing");
    } catch (error) {
      // eslint-disable-next-line no-console -- surfaces stuffing request failures
      console.error("Stuffing request failed:", error);
      setStuffingInitial({ data: { errors: [] }, errorMessages: [] });
      setRoute("stuffing");
    } finally {
      setStuffingLoading(false);
    }
  };

  const handleNext = () => {
    if (stuffingLoading || palletCheckLoader) return;

    if (route === "product") {
      void setRouteHandler("container");
      return;
    }

    if (route === "container") {
      const hasTransport = Boolean(containers[0] || autoContainers[0]);
      const hasProduct = Boolean(groups[0]?.items?.[0]);

      if (hasTransport && hasProduct) {
        void goToStuffing();
        return;
      }

      Notification("Service message", "Please add at least one container and product.", "open");
    }
  };

  const handleBack = () => {
    if (stuffingLoading || palletCheckLoader) return;

    if (route === "container") {
      void setRouteHandler("product");
    } else if (route === "stuffing") {
      void setRouteHandler("container");
    }
  };

  const currentStep = routeToStep(route);
  const hasCargo = packingGroups.some((group) => group.cargoRows.length > 0);
  const hasTransport = Boolean(containers[0] || autoContainers[0]);
  const nextDisabled =
    stuffingLoading ||
    palletCheckLoader ||
    route === "stuffing" ||
    (route === "product" && !hasCargo) ||
    (route === "container" && !hasTransport);

  return (
    <AppShell
      currentStep={currentStep}
      onNext={route === "stuffing" ? undefined : handleNext}
      onBack={route === "product" ? undefined : handleBack}
      nextDisabled={nextDisabled}
    >
      {route === "product" && <PackingList />}
      {route === "container" && <ContainerPage />}
      {route === "stuffing" && stuffingInitial && (
        <StuffingPage setRoute={setRouteHandler} initialStuffing={stuffingInitial} />
      )}

      <Modal isOpen={palletCheckLoader} onClose={() => undefined} centered modalClassname={loaderStyles.modal}>
        <StuffingCalculationLoader
          title="Validating pallets"
          description="Checking that your cargo fits on the selected pallets"
        />
      </Modal>

      <Modal isOpen={stuffingLoading} onClose={() => undefined} centered modalClassname={loaderStyles.modal}>
        <StuffingCalculationLoader />
      </Modal>
    </AppShell>
  );
};

export default App;
